import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, {
  IBrandingApi,
  IColorScheme,
} from '@services/BrandingsService';

import { useEditorMode } from '../EditorModeContext';
import { useDrift } from '../DriftContext';
import { useEditorStyles } from '../styles';
import { useBrandingBarStyles } from './styles';
import QuickSwitcher from './QuickSwitcher';
import DriftSyncSheet from './DriftSyncSheet';

interface BrandingBarProps {
  onOpenLibrary?: () => void;
  onCreateNew?: () => void;
}

const BrandingBar = ({ onOpenLibrary, onCreateNew }: BrandingBarProps) => {
  const { t: translate } = useTranslation();
  const editorStyles = useEditorStyles();
  const styles = useBrandingBarStyles();

  const { isQuickSwitcherOpen, openQuickSwitcher, autosaveStatus } =
    useEditorMode();
  const { rollup, hasTemplate } = useDrift();
  const [isSyncSheetOpen, setIsSyncSheetOpen] = useState(false);

  const {
    currentRoom,
    currentBrandingId,
    setIsBrandingModeEnabled,
    setRoomSpots,
    setBrandingSpots,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();

  const mapId = currentRoom?.map?.mapId;
  const activeBrandingId = currentRoom?.activeBrandingId;
  const isLive = !!currentBrandingId && currentBrandingId === activeBrandingId;

  const [branding, setBranding] = useState<IBrandingApi | null>(null);
  const brandButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!currentBrandingId || !mapId) {
      setBranding(null);
      return;
    }
    let cancelled = false;
    BrandingsService.getBrandings({ mapId, limit: 100 })
      .then((res) => {
        if (cancelled) {
          return;
        }
        const match =
          res.brandings?.find((b) => b.brandingId === currentBrandingId) ??
          null;
        setBranding(match);
      })
      .catch((loadError) => {
        console.error(
          `Failed to load branding meta: ${(loadError as Error)?.message}`,
          { brandingId: currentBrandingId },
        );
      });
    return () => {
      cancelled = true;
    };
  }, [currentBrandingId, mapId, autosaveStatus]);

  const brandingName = useMemo(() => {
    return branding?.name || translate('adminToolbox.unnamed');
  }, [branding, translate]);

  const handleExit = () => {
    setIsBrandingModeEnabled(false);
    setRoomSpots([]);
    setBrandingSpots([]);
    sendMessageToUnreal({ action: 'LEAVE_CUSTOMIZATION_MODE' });
  };

  const renderSwatch = (cs: IColorScheme | undefined) => {
    const hasAny = cs?.primaryColor || cs?.secondaryColor || cs?.outlineColor;
    if (!hasAny) {
      return (
        <Box sx={styles.brandSwatchRow}>
          <Box sx={styles.brandSwatchPlaceholder} />
        </Box>
      );
    }
    return (
      <Box sx={styles.brandSwatchRow}>
        <Box
          sx={[
            styles.brandSwatchStripe,
            { backgroundColor: cs?.primaryColor || 'rgba(255,255,255,0.1)' },
          ]}
        />
        <Box
          sx={[
            styles.brandSwatchStripe,
            { backgroundColor: cs?.secondaryColor || 'rgba(255,255,255,0.1)' },
          ]}
        />
        <Box
          sx={[
            styles.brandSwatchStripe,
            { backgroundColor: cs?.outlineColor || 'rgba(255,255,255,0.1)' },
          ]}
        />
      </Box>
    );
  };

  const renderAutosave = () => {
    if (autosaveStatus === 'saving') {
      return (
        <>
          <CircularProgress size={10} sx={{ color: 'rgba(255,255,255,0.6)' }} />
          <span>{translate('editorMode.saving')}</span>
        </>
      );
    }
    if (autosaveStatus === 'error') {
      return (
        <>
          <ErrorOutlineIcon sx={{ fontSize: 12, color: '#FF6B6B' }} />
          <span>{translate('editorMode.saveError')}</span>
        </>
      );
    }

    return (
      <>
        <CheckCircleOutlineIcon sx={{ fontSize: 12, color: '#1DAB56' }} />
        <span>{translate('editorMode.saved')}</span>
      </>
    );
  };

  return (
    <>
      <Box
        sx={[editorStyles.glassPanel, styles.bar]}
        ref={brandButtonRef as React.RefObject<HTMLDivElement>}
      >
        <Box sx={styles.brandButton} onClick={openQuickSwitcher}>
          {renderSwatch(branding?.brandingColors)}
          <Typography sx={styles.brandName}>{brandingName}</Typography>
          <KeyboardArrowDownIcon
            sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }}
          />
        </Box>

        <Box sx={styles.divider} />
        <Box sx={styles.autosaveRow}>{renderAutosave()}</Box>

        {hasTemplate ? (
          <>
            <Box sx={styles.divider} />
            <Box
              sx={[
                styles.driftPill,
                rollup.state === 'outdated'
                  ? styles.driftPillRed
                  : rollup.state === 'behind'
                  ? styles.driftPillAmber
                  : styles.driftPillGreen,
              ]}
              onClick={() => setIsSyncSheetOpen(true)}
              role="button"
              aria-label={translate('editorMode.driftReview')}
            >
              {rollup.state === 'outdated' ? (
                <ErrorOutlineIcon sx={{ fontSize: 15 }} />
              ) : rollup.state === 'behind' ? (
                <SyncOutlinedIcon sx={{ fontSize: 15 }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
              )}
              <Typography sx={styles.driftPillLabel}>
                {rollup.state === 'outdated'
                  ? translate('editorMode.driftOutdated')
                  : rollup.state === 'behind'
                  ? translate('editorMode.driftBehind', {
                      count: rollup.missingSpots + rollup.newScripts,
                    })
                  : translate('editorMode.driftUpToDate')}
              </Typography>
            </Box>
          </>
        ) : null}

        <Box sx={styles.divider} />

        <Box sx={styles.activeLabel}>
          <Box
            sx={[
              styles.activeLabelDot,
              !isLive && styles.activeLabelDotInactive,
            ]}
          />
          {isLive
            ? translate('adminToolbox.onAir')
            : translate('editorMode.notLive')}
        </Box>

        <Tooltip title={translate('adminToolbox.exitEditorMode')}>
          <IconButton sx={styles.exitButton} onClick={handleExit}>
            <ExitToAppIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <QuickSwitcher
        anchorEl={isQuickSwitcherOpen ? brandButtonRef.current : null}
        onCreateNew={onCreateNew}
        onOpenLibrary={onOpenLibrary}
      />

      <DriftSyncSheet
        open={isSyncSheetOpen}
        onClose={() => setIsSyncSheetOpen(false)}
      />
    </>
  );
};

export default BrandingBar;
